import type { Grouping } from '@sembo-private/asq';
import { asq, groupBy, map } from '@sembo-private/asq';

// TODO: modules/ should not depend on external code
import { cnxCompare } from '@apps/main/modules/helpers/comparisons';

import { assertValue } from '@libs/utils';
import type {
  Money,
  CancellationPolicy,
  TaxesAndFee,
  HotelBookableAndLockableOption,
} from '@libs/trip-planner-interfaces';

import type {
  PriceBreakdownForRoom,
  PriceBreakdownModalData,
  StayPriceBreakdownData,
} from './models';

export function getPriceBreakdownModalData(
  stay: StayPriceBreakdownData
): PriceBreakdownModalData {
  const allTaxesAndFees = getAllTaxesAndFees(stay);
  const taxesAndFeesSummary = getTaxesAndFeesSummary(allTaxesAndFees);
  const cancellationPoliciesSummary =
    getCancellationPoliciesSummarySorted(stay);
  const priceAtLocation = getPriceAtLocation(allTaxesAndFees);
  const priceExcludingTaxes = getPriceExcludingTaxes(
    stay.price,
    allTaxesAndFees
  );
  const forRooms = getSummaryForRooms(stay);

  return {
    summary: {
      priceBreakdown: {
        taxesAndFees: taxesAndFeesSummary,
        priceAtLocation,
        priceExcludingTaxes,
        totalPrice: stay.price,
      },
      cancellationPolicies: cancellationPoliciesSummary,
    },
    forRooms,
  };
}

function getAllTaxesAndFees(stay: StayPriceBreakdownData): TaxesAndFee[] {
  return stay.rooms.flatMap((room) => room.taxesAndFees);
}

function getTaxesAndFeesSummary(allTaxesAndFees: TaxesAndFee[]): TaxesAndFee[] {
  return asq(allTaxesAndFees)
    .pipe(
      groupBy((tax) => tax.type),
      map<Grouping<string, TaxesAndFee>, TaxesAndFee>((group) => {
        const groupedTaxes = asq(group).toArray();
        const firstGroupedTaxes = groupedTaxes[0];

        assertValue(
          firstGroupedTaxes,
          `cancellationPoliciesUtils.ts > getTaxesAndFeesSummary > firstGroupedTaxes`
        );
        return {
          amount: {
            amount: groupedTaxes.reduce(
              (total, tax) => total + tax.amount.amount,
              0
            ),
            currency: firstGroupedTaxes.amount.currency,
            currencySymbol: firstGroupedTaxes.amount.currencySymbol,
          },
          type: group.key,
          isIncludedInPrice: firstGroupedTaxes.isIncludedInPrice,
        };
      })
    )
    .toArray();
}

function getAllCancellationPolicies(
  stay: StayPriceBreakdownData | HotelBookableAndLockableOption
): CancellationPolicy[] {
  return stay.rooms.flatMap((room) => room.cancellationPolicies);
}

function getCancellationPoliciesSummary(
  allCancellationPolicies: CancellationPolicy[]
): CancellationPolicy[] {
  return asq(allCancellationPolicies)
    .pipe(
      groupBy((policy) => policy.fromDate),
      map<Grouping<string, CancellationPolicy>, CancellationPolicy>((group) => {
        const groupedPolicies = asq(group).toArray();
        const firstGroupedPolicies = groupedPolicies[0];

        assertValue(
          firstGroupedPolicies,
          `cancellationPoliciesUtils.ts > getCancellationPoliciesSummary > firstGroupedPolicies`
        );
        return {
          fromDate: group.key,
          fromDateIso: firstGroupedPolicies.fromDateIso,
          amount: {
            amount: groupedPolicies.reduce(
              (total, policy) => total + policy.amount.amount,
              0
            ),
            currency: firstGroupedPolicies.amount.currency,
            currencySymbol: firstGroupedPolicies.amount.currencySymbol,
          },
        };
      })
    )
    .toArray()
    .sort(cnxCompare);
}

function getCancellationPoliciesSummarySorted(
  stay: StayPriceBreakdownData | HotelBookableAndLockableOption
): CancellationPolicy[] {
  const allCancellationPolicies = getAllCancellationPolicies(stay);

  return getCancellationPoliciesSummary(allCancellationPolicies);
}

function getPriceAtLocation(taxesAndFees: TaxesAndFee[]): Money | undefined {
  const totalTaxesNotIncluded = taxesAndFees.reduce(
    (total: Money, tax: TaxesAndFee) => {
      if (!tax.isIncludedInPrice) {
        total.amount += tax.amount.amount;
        total.currency = tax.amount.currency;
        total.currencySymbol = tax.amount.currencySymbol;
      }

      return total;
    },
    { amount: 0, currency: '', currencySymbol: '' }
  );

  if (
    !totalTaxesNotIncluded.amount ||
    !totalTaxesNotIncluded.currency ||
    !totalTaxesNotIncluded.currencySymbol
  ) {
    return;
  }

  return totalTaxesNotIncluded;
}

function getPriceExcludingTaxes(
  totalPrice: Money,
  taxesAndFees: TaxesAndFee[]
): Money | undefined {
  const totalTaxesIncludedInPrice = taxesAndFees.reduce(
    (total, tax) => (total += tax.isIncludedInPrice ? tax.amount.amount : 0),
    0
  );

  return totalTaxesIncludedInPrice > 0
    ? {
        amount: totalPrice.amount - totalTaxesIncludedInPrice,
        currency: totalPrice.currency,
        currencySymbol: totalPrice.currencySymbol,
      }
    : undefined;
}

function getSummaryForRooms(
  stay: StayPriceBreakdownData
): PriceBreakdownForRoom[] {
  return stay.rooms.map((room) => {
    const priceAtLocation = getPriceAtLocation(room.taxesAndFees);

    const priceExcludingTaxes = getPriceExcludingTaxes(
      room.price,
      room.taxesAndFees
    );

    return {
      roomName: room.name,
      roomSummary: {
        priceBreakdown: {
          priceAtLocation,
          priceExcludingTaxes,
          taxesAndFees: room.taxesAndFees,
          totalPrice: room.price,
        },
        cancellationPolicies: room.cancellationPolicies,
      },
    };
  });
}
